import { useMemo } from 'react';
import clsx from 'clsx';
import type { LinkPreview, Message } from '@flock-types/chat.types';
import {
  buildOptimisticLinkPreview,
  extractFirstUrl,
  shouldShowGenericFallbackPreview,
  stripPreviewUrlFromText,
} from '@/lib/linkEmbeds';
import { formatMessageTime } from '@/lib/chatTime';
import LinkPreviewCard from './embeds/LinkPreviewCard';

function createdAtDate(message: Message): Date | null {
  const ts: any = message.createdAt;
  if (!ts) return null;
  if (typeof ts === 'object' && ts.toDate) return ts.toDate();
  const d = new Date(ts);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Render message text with @mention highlights from stored offsets. */
function MentionText({ message, text, own }: { message: Message; text: string; own: boolean }) {
  const segments = useMemo(() => {
    const mentions = [...(message.mentions || [])]
      .filter((m) => m.startIndex >= 0 && m.endIndex <= text.length && m.startIndex < m.endIndex)
      // Offsets reference the ORIGINAL text — after URL stripping they may not
      // line up, so validate the slice still matches the mention token.
      .filter((m) => text.slice(m.startIndex, m.endIndex) === `@${m.userName}`)
      .sort((a, b) => a.startIndex - b.startIndex);
    if (mentions.length === 0) return [{ text, mention: false }];
    const out: Array<{ text: string; mention: boolean }> = [];
    let cursor = 0;
    for (const m of mentions) {
      if (m.startIndex > cursor) out.push({ text: text.slice(cursor, m.startIndex), mention: false });
      out.push({ text: text.slice(m.startIndex, m.endIndex), mention: true });
      cursor = m.endIndex;
    }
    if (cursor < text.length) out.push({ text: text.slice(cursor), mention: false });
    return out;
  }, [text, message.mentions]);

  return (
    <span className="whitespace-pre-wrap break-words">
      {segments.map((seg, i) =>
        seg.mention ? (
          <span
            key={i}
            className={clsx(
              'rounded px-0.5 font-semibold',
              own ? 'bg-white/20 text-white' : 'bg-flock-navy-100 text-flock-navy-700'
            )}
          >
            {seg.text}
          </span>
        ) : (
          <span key={i}>{seg.text}</span>
        )
      )}
    </span>
  );
}

interface Props {
  message: Message;
  own: boolean;
  onReplyClick?: (messageId: string) => void;
  /** Media renderers (images, GIFs, files, scripture) — injected by the pane. */
  renderRichContent?: (message: Message, own: boolean) => React.ReactNode;
}

export default function MessageBubble({ message, own, onReplyClick, renderRichContent }: Props) {
  const date = createdAtDate(message);
  const content = message.content || {};

  // Derive the link preview exactly like mobile MessageBubble: stored preview
  // first, optimistic preview for known providers, generic fallback only for
  // fresh messages (old links without stored previews stay plain).
  const derivedLinkPreview = useMemo((): LinkPreview | undefined => {
    if (message.type === 'image' || message.type === 'gif' || message.type === 'file') {
      return undefined;
    }
    if (content.linkPreview?.url) return content.linkPreview;

    const url = extractFirstUrl(content.userText || content.text);
    if (!url) return undefined;

    const optimistic = buildOptimisticLinkPreview(url);
    if (
      optimistic.provider === 'youtube' ||
      optimistic.provider === 'x' ||
      optimistic.provider === 'tiktok' ||
      optimistic.provider === 'facebook'
    ) {
      return optimistic;
    }
    return shouldShowGenericFallbackPreview(url, message.createdAt) ? optimistic : undefined;
  }, [content.linkPreview, content.text, content.userText, message.type, message.createdAt]);

  // Scripture messages store the formatted verse in content.text as a
  // fallback; the card renders the verse, so show only the user's own words.
  // Link messages drop the raw URL from the text (the card carries it).
  const displayText = useMemo(() => {
    const base = content.scripture ? content.userText : content.text;
    return stripPreviewUrlFromText(base, derivedLinkPreview);
  }, [content.scripture, content.userText, content.text, derivedLinkPreview]);

  const hasText = Boolean(displayText && displayText.trim());
  const rich = renderRichContent?.(message, own);

  return (
    <div
      className={clsx(
        'max-w-xl rounded-2xl px-3.5 py-2 text-sm shadow-sm',
        own ? 'rounded-br-md bg-flock-navy-600 text-white' : 'rounded-bl-md bg-white text-flock-navy-900'
      )}
    >
      {message.replyTo && (
        <button
          onClick={() => onReplyClick?.(message.replyTo!.messageId)}
          className={clsx(
            'mb-1.5 block w-full rounded-lg border-l-2 px-2 py-1 text-left text-xs',
            own
              ? 'border-white/40 bg-white/10 text-white/80'
              : 'border-flock-navy-300 bg-flock-navy-50 text-flock-navy-500'
          )}
        >
          <span className="font-semibold">{message.replyTo.userName}</span>
          <span className="block truncate">{message.replyTo.text}</span>
        </button>
      )}

      {rich}

      {hasText && <MentionText message={message} text={displayText} own={own} />}

      {derivedLinkPreview && <LinkPreviewCard preview={derivedLinkPreview} own={own} />}

      <div
        className={clsx(
          'mt-0.5 select-none text-[10px]',
          derivedLinkPreview || !hasText ? 'block' : 'ml-2 inline-block translate-y-0.5 whitespace-nowrap',
          own ? 'text-right text-white/60' : 'text-flock-navy-300'
        )}
        title={date ? formatMessageTime(date) : undefined}
      >
        {date
          ? date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
          : 'sending…'}
        {message.isEdited ? ' · edited' : ''}
      </div>
    </div>
  );
}
